import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Alert,
  CircularProgress,
  Divider,
  FormControlLabel,
  Switch,
  Paper,
  Chip,
  Avatar,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  FormLabel,
  FormGroup
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  DeliveryDining as CourierIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import ownAuthSession from '../../services/auth/ownAuthSession';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { mainApi } from '../../config/apiClient';
import {
  getPersonnel as getPersonnelSql,
  setScreenPermission,
  updatePersonnel,
} from '../../services/migration/personnelDataService';

// Personel veri tipi
interface Personnel {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  role?: string;
  branchesId?: string;
  branchName?: string;
  departmentId?: string;
  departmentName?: string;
  jobTitle?: string;
}

// Kurye yetkileri tipi
interface CourierPermissions {
  accessCourierApp: boolean; // Kurye uygulamasına erişim yetkisi
  viewAssignedOrders: boolean; // Atanan siparişleri görme yetkisi
  updateOrderStatus: boolean; // Sipariş durumunu güncelleme yetkisi
  viewCustomerInfo: boolean; // Müşteri bilgilerini görme yetkisi
  callCustomer: boolean; // Müşteriyi arama yetkisi
  viewOrderDetails: boolean; // Sipariş detaylarını görme yetkisi
  markAsDelivered: boolean; // Teslim edildi olarak işaretleme yetkisi
  reportIssues: boolean; // Sorun bildirme yetkisi
  viewEarnings: boolean; // Kazançları görme yetkisi
}

interface AddCourierModalProps {
  open: boolean;
  onClose: () => void;
  onCourierAdded?: () => void;
}

const AddCourierModal: React.FC<AddCourierModalProps> = ({
  open,
  onClose,
  onCourierAdded
}) => {
  const { t } = useTranslation('salesSystem');
  const { userDetails } = useAuth();
  
  // State tanımlamaları
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  
  // Personel listesi ve seçim
  const [allPersonnel, setAllPersonnel] = useState<Personnel[]>([]);
  const [filteredPersonnel, setFilteredPersonnel] = useState<Personnel[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPersonnelIds, setSelectedPersonnelIds] = useState<string[]>([]);
  const [selectedPersonnelForPermissions, setSelectedPersonnelForPermissions] = useState<string | null>(null);
  
  // Yetki yönetimi
  const [personnelPermissionsMap, setPersonnelPermissionsMap] = useState<{ [personnelId: string]: CourierPermissions }>({});

  // Varsayılan kurye yetkileri
  const defaultCourierPermissions: CourierPermissions = {
    accessCourierApp: true,
    viewAssignedOrders: true,
    updateOrderStatus: true,
    viewCustomerInfo: true,
    callCustomer: true,
    viewOrderDetails: true,
    markAsDelivered: true,
    reportIssues: true,
    viewEarnings: true
  };

  // Modal açıldığında şirket ID'sini AuthContext'ten (SQL/own-auth oturumu) al.
  useEffect(() => {
    if (!open) return;
    // Modal açıldığında state'leri temizle
    setError(null);
    setSuccess(null);
    setLoading(false);

    const uid = ownAuthSession.getCurrentUid();
    if (!uid) {
      console.warn('⚠️ Kullanıcı oturum açmamış');
      setError(t('courierAddModal.errors.noAuthenticatedUser'));
      return;
    }
    setCompanyId(userDetails?.companyId || null);
  }, [open, userDetails]);

  // Personelleri yükle
  useEffect(() => {
    const fetchPersonnel = async () => {
      if (!companyId) return;
      
      setLoading(true);
      try {
        // Faz 3: personel listesi SQL'den (personnelDataService).
        const sqlPersonnel = await getPersonnelSql(companyId);
        const personnelList: Personnel[] = (sqlPersonnel || []).map((data: any) => ({
          id: data.id, // Firebase UID
          name: data.name || t('courierAddModal.defaults.unnamedStaff'),
          email: data.email || '',
          phone: data.phone || '',
          role: data.role || 'employee',
          branchesId: data.branchesId || null,
          branchName: '',
          departmentId: data.departmentId != null ? String(data.departmentId) : undefined,
          departmentName: '',
          jobTitle: data.jobTitle || ''
        }));
        
        // Admin ve manager rolüne sahip personelleri filtrele, sadece employee'leri göster
        const filteredList = personnelList.filter(person => 
          person.role === 'employee' || person.role === '' || !person.role
        );
        filteredList.sort((a, b) => a.name.localeCompare(b.name));
        
        setAllPersonnel(filteredList);
      } catch (error) {
        console.error('Personeller yüklenirken hata:', error);
        setError(t('courierAddModal.errors.staffListLoadFailed'));
      } finally {
        setLoading(false);
      }
    };
    
    if (companyId) {
      fetchPersonnel();
    }
  }, [companyId]);

  // Arama filtresi
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredPersonnel(allPersonnel);
    } else {
      const filtered = allPersonnel.filter(person =>
        person.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (person.email && person.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (person.phone && person.phone.includes(searchTerm))
      );
      setFilteredPersonnel(filtered);
    }
  }, [searchTerm, allPersonnel]);

  // Personel seçimi değiştiğinde
  const handlePersonnelSelectionChange = (personnelId: string, checked: boolean) => {
    if (checked) {
      setSelectedPersonnelIds(prev => [...prev, personnelId]);
      // Varsayılan yetkileri ata
      setPersonnelPermissionsMap(prev => ({
        ...prev,
        [personnelId]: { ...defaultCourierPermissions }
      }));
    } else {
      setSelectedPersonnelIds(prev => prev.filter(id => id !== personnelId));
      // Yetkilerini kaldır
      setPersonnelPermissionsMap(prev => {
        const updated = { ...prev };
        delete updated[personnelId];
        return updated;
      });
      // Eğer şu an seçili olan personel kaldırıldıysa, seçimi temizle
      if (selectedPersonnelForPermissions === personnelId) {
        setSelectedPersonnelForPermissions(null);
      }
    }
  };

  // Yetki düzenleme için personel seçimi
  const handleSelectPersonnelForPermissions = (personnelId: string) => {
    setSelectedPersonnelForPermissions(personnelId);
    
    // Eğer bu personel için henüz yetki ayarları yoksa, varsayılan değerleri oluştur
    if (!personnelPermissionsMap[personnelId]) {
      setPersonnelPermissionsMap(prev => ({
        ...prev,
        [personnelId]: { ...defaultCourierPermissions }
      }));
    }
  };

  // Yetki değişikliği
  const handlePermissionChange = (personnelId: string, permission: string, value: boolean) => {
    setPersonnelPermissionsMap(prev => ({
      ...prev,
      [personnelId]: {
        ...prev[personnelId],
        [permission]: value
      }
    }));
  };

  // SQL'e kurye kaydetme (yeni .NET API endpoint'i)
  const registerCourierToSQL = async (personnelIds: string[]) => {
    try {
      console.log('🔄 SQL\'e kurye kayıtları yapılıyor...', {
        personnelIds,
        personnelCount: personnelIds.length
      });

      // own-auth token'ını al
      const token = await ownAuthSession.getAccessToken();
      if (!token) {
        throw new Error('No authenticated user found');
      }
      
      // Personel bilgilerini al ve kurye kayıt verilerini hazırla
      const courierRegistrations = await Promise.all(
        personnelIds.map(async (personnelId) => {
          const permissions = personnelPermissionsMap[personnelId] || defaultCourierPermissions;
          
          // Personelin şube bilgisini ve diğer bilgilerini al
          const personnel = allPersonnel.find(p => p.id === personnelId);
          const branchIds = personnel?.branchesId ? [personnel.branchesId] : [];
          
          // Default sipariş ayarları oluştur
          const orderSettings = {
            branchIds: branchIds,
            restaurantOrders: true,
            ecommerceOrders: true,
            lastUpdated: new Date().toISOString(),
            updatedBy: ownAuthSession.getCurrentUid() || ''
          };
          
          console.log(`📋 ${personnelId} için kurye kaydı hazırlanıyor`, {
            name: personnel?.name || 'Unnamed',
            email: personnel?.email || '',
            phone: personnel?.phone || '',
            branchIds,
            permissions,
            orderSettings
          });
          
          return {
            firebaseUid: personnelId,
            name: personnel?.name || 'Unnamed Courier',
            email: personnel?.email || '',
            phoneNumber: personnel?.phone || '',
            branchIds: branchIds,
            restaurantOrders: true,
            ecommerceOrders: true,
            permissions: permissions,
            orderSettings: orderSettings
          };
        })
      );
      
      // Bulk kurye kayıt API'sini çağır
      const apiResponse = await mainApi.post(
        `/api/couriers/register-bulk`,
        {
          companyId: companyId,
          couriers: courierRegistrations
        }
      );

      if (apiResponse.status !== 200) {
        throw new Error(apiResponse.data?.message || 'SQL kurye kayıt başarısız');
      }

      console.log('✅ SQL\'e kurye kayıtları başarıyla tamamlandı', {
        processedPersonnel: personnelIds,
        totalCount: personnelIds.length,
        sqlResults: apiResponse.data
      });

      return apiResponse.data;
    } catch (error) {
      console.error('❌ SQL kurye kayıt hatası:', error);
      
      if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.message || error.message;
        throw new Error(`SQL kurye kayıt hatası: ${errorMessage}`);
      }
      
      throw new Error(`Error while saving courier records to SQL: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Kurye rolü atama — .NET HR API (SystemUsers.Role = 'courier'). Node/Firebase yok.
  const assignCourierRoleViaAPI = async (personnelIds: string[]) => {
    try {
      console.log('🔄 SQL HR API ile kurye rolleri atanıyor...', {
        personnelIds,
        personnelCount: personnelIds.length
      });

      await Promise.all(
        personnelIds.map((personnelId) => updatePersonnel(personnelId, { role: 'courier' }))
      );

      console.log('✅ SQL HR API ile kurye rolleri başarıyla atandı', {
        processedPersonnel: personnelIds,
        totalCount: personnelIds.length
      });

      return personnelIds;
    } catch (error) {
      console.error('❌ Kurye rolü atama hatası:', error);
      
      if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.message || error.message;
        throw new Error(`Kurye rolü atama hatası: ${errorMessage}`);
      }
      
      throw new Error(`Error while assigning courier role: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Kurye kaydetme işlemi (SQL + Firebase + API)
  const saveCourierData = async () => {
    if (!companyId) {
      throw new Error(t('courierAddModal.errors.companyIdNotFound'));
    }
    
    try {
      console.log('🚀 Kurye kaydetme işlemi başlatılıyor...', {
        companyId,
        selectedPersonnelIds,
        personnelCount: selectedPersonnelIds.length
      });
      
      // 1. Önce SQL'e kurye kayıtlarını yap (default sipariş ayarları ile)
      console.log('📝 1. Adım: SQL\'e kurye kayıtları yapılıyor...');
      const sqlResults = await registerCourierToSQL(selectedPersonnelIds);
      
      // 2. SQL HR API ile kurye rollerini ata (SystemUsers.Role = 'courier')
      console.log('🔑 2. Adım: SQL HR API ile kurye rolleri atanıyor...');
      await assignCourierRoleViaAPI(selectedPersonnelIds);
      
      // 3. Faz 3: kurye yetkilerini SQL'e kaydet (SystemUserScreenPermissions screenType='courier').
      //    Rol/durum bilgisi SQL kurye kaydi (registerCourierToSQL) + assign-courier API tarafindan yonetilir.
      console.log('📝 3. Adım: SQL\'e kurye yetkileri kaydediliyor...');
      const updatePromises = selectedPersonnelIds.map(async (personnelId) => {
        const permissions = personnelPermissionsMap[personnelId] || defaultCourierPermissions;
        await setScreenPermission(personnelId, 'courier', null, true, permissions);
        return personnelId;
      });
      
      const firebaseResults = await Promise.all(updatePromises);
      
      console.log('🎉 Kurye kaydetme işlemi tamamlandı', {
        sqlResults: sqlResults,
        firebaseResults: firebaseResults,
        processedPersonnel: selectedPersonnelIds,
        totalCount: selectedPersonnelIds.length
      });
      
      return {
        sqlResults,
        firebaseResults,
        totalCount: selectedPersonnelIds.length
      };
    } catch (error) {
      console.error('❌ Kurye kaydetme hatası:', error);
      throw new Error(`An error occurred while saving courier: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Kaydetme işlemi
  const handleSave = async () => {
    // Validasyon kontrolleri
    if (selectedPersonnelIds.length === 0) {
      setError(t('courierAddModal.errors.selectAtLeastOneStaff'));
      return;
    }

    // Her seçili personel için yetki kontrolü
    const missingPermissions = selectedPersonnelIds.filter(id => !personnelPermissionsMap[id]);
    if (missingPermissions.length > 0) {
      setError(t('courierAddModal.errors.configureAllPermissions'));
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      console.log('🚀 Kurye kaydetme işlemi başlatılıyor...', {
        selectedCount: selectedPersonnelIds.length,
        companyId,
        timestamp: new Date().toISOString()
      });

      // Yeni entegre kurye kaydetme işlemi (SQL + Firebase + API)
      const results = await saveCourierData();
      
      const successMessage = t('courierAddModal.success.assignedCount', { count: results.totalCount });
      setSuccess(successMessage);
      
      console.log('🎉 Kurye kaydetme işlemi tamamlandı', {
        sqlResults: results.sqlResults,
        firebaseResults: results.firebaseResults,
        totalCount: results.totalCount,
        successMessage
      });
      
      // 3 saniye sonra modalı kapat ve parent component'i bilgilendir
      setTimeout(() => {
        onCourierAdded?.();
        handleClose();
      }, 3000);
      
    } catch (error) {
      console.error('❌ Kurye kaydetme hatası:', error);
      const errorMessage = error instanceof Error ? error.message : t('courierAddModal.errors.unknownSaveError');
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Modal kapatma
  const handleClose = () => {
    setSelectedPersonnelIds([]);
    setSelectedPersonnelForPermissions(null);
    setPersonnelPermissionsMap({});
    setSearchTerm('');
    setError(null);
    setSuccess(null);
    onClose();
  };

  // Seçili personeli bul
  const selectedPersonnelForPermissionsData = selectedPersonnelForPermissions 
    ? allPersonnel.find(p => p.id === selectedPersonnelForPermissions)
    : null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #eee',
        pb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CourierIcon sx={{ color: 'primary.main', mr: 1 }} />
          <Typography variant="h6">{t('courierAddModal.title')}</Typography>
        </Box>
        <IconButton edge="end" color="inherit" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {/* Personel Seçimi */}
            <Typography variant="h6" gutterBottom>
              {t('courierAddModal.staffSelection.title', { count: selectedPersonnelIds.length })}
            </Typography>
            
            <TextField
              fullWidth
              size="small"
              placeholder={t('courierAddModal.staffSelection.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <Paper sx={{ maxHeight: 300, overflow: 'auto', mb: 3 }}>
              <List dense>
                {filteredPersonnel.map((person) => (
                  <ListItem key={person.id}>
                    <Checkbox
                      checked={selectedPersonnelIds.includes(person.id)}
                      onChange={(e) => handlePersonnelSelectionChange(person.id, e.target.checked)}
                    />
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ width: 32, height: 32, mr: 1, fontSize: 14 }}>
                            {person.name.charAt(0)}
                          </Avatar>
                          <Typography variant="body2" fontWeight="medium">
                            {person.name}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box>
                          {person.email && (
                            <Typography variant="caption" color="text.secondary">
                              {person.email}
                            </Typography>
                          )}
                          {person.phone && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              {person.phone}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    {selectedPersonnelIds.includes(person.id) && (
                      <ListItemSecondaryAction>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleSelectPersonnelForPermissions(person.id)}
                          color={selectedPersonnelForPermissions === person.id ? 'primary' : 'inherit'}
                        >
                          {t('courierAddModal.staffSelection.editPermissions')}
                        </Button>
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                ))}
                {filteredPersonnel.length === 0 && (
                  <ListItem>
                    <ListItemText
                      primary={t('courierAddModal.staffSelection.noStaffFound')}
                      secondary={t('courierAddModal.staffSelection.noStaffMatches')}
                    />
                  </ListItem>
                )}
              </List>
            </Paper>

            {/* Yetki Düzenleme */}
            {selectedPersonnelForPermissions && selectedPersonnelForPermissionsData && (
              <Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {t('courierAddModal.permissions.title', { name: selectedPersonnelForPermissionsData.name })}
                </Typography>
                
                <Paper sx={{ p: 2 }}>
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend">{t('courierAddModal.permissions.legend')}</FormLabel>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.accessCourierApp || false}
                            onChange={(e) => handlePermissionChange(selectedPersonnelForPermissions, 'accessCourierApp', e.target.checked)}
                          />
                        }
                        label={t('courierAddModal.permissions.items.accessCourierApp')}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.viewAssignedOrders || false}
                            onChange={(e) => handlePermissionChange(selectedPersonnelForPermissions, 'viewAssignedOrders', e.target.checked)}
                          />
                        }
                        label={t('courierAddModal.permissions.items.viewAssignedOrders')}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.updateOrderStatus || false}
                            onChange={(e) => handlePermissionChange(selectedPersonnelForPermissions, 'updateOrderStatus', e.target.checked)}
                          />
                        }
                        label={t('courierAddModal.permissions.items.updateOrderStatus')}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.viewCustomerInfo || false}
                            onChange={(e) => handlePermissionChange(selectedPersonnelForPermissions, 'viewCustomerInfo', e.target.checked)}
                          />
                        }
                        label={t('courierAddModal.permissions.items.viewCustomerInfo')}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.callCustomer || false}
                            onChange={(e) => handlePermissionChange(selectedPersonnelForPermissions, 'callCustomer', e.target.checked)}
                          />
                        }
                        label={t('courierAddModal.permissions.items.callCustomer')}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.viewOrderDetails || false}
                            onChange={(e) => handlePermissionChange(selectedPersonnelForPermissions, 'viewOrderDetails', e.target.checked)}
                          />
                        }
                        label={t('courierAddModal.permissions.items.viewOrderDetails')}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.markAsDelivered || false}
                            onChange={(e) => handlePermissionChange(selectedPersonnelForPermissions, 'markAsDelivered', e.target.checked)}
                          />
                        }
                        label={t('courierAddModal.permissions.items.markAsDelivered')}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.reportIssues || false}
                            onChange={(e) => handlePermissionChange(selectedPersonnelForPermissions, 'reportIssues', e.target.checked)}
                          />
                        }
                        label={t('courierAddModal.permissions.items.reportIssues')}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={personnelPermissionsMap[selectedPersonnelForPermissions]?.viewEarnings || false}
                            onChange={(e) => handlePermissionChange(selectedPersonnelForPermissions, 'viewEarnings', e.target.checked)}
                          />
                        }
                        label={t('courierAddModal.permissions.items.viewEarnings')}
                      />
                    </FormGroup>
                  </FormControl>
                </Paper>
              </Box>
            )}

            {/* Seçili Personeller Özeti */}
            {selectedPersonnelIds.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {t('courierAddModal.selectedStaff.title', { count: selectedPersonnelIds.length })}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedPersonnelIds.map(id => {
                    const person = allPersonnel.find(p => p.id === id);
                    return person ? (
                      <Chip
                        key={id}
                        label={person.name}
                        onDelete={() => handlePersonnelSelectionChange(id, false)}
                        color="primary"
                        variant="outlined"
                      />
                    ) : null;
                  })}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #eee' }}>
        <Button onClick={handleClose} disabled={saving}>
          {t('courierAddModal.actions.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || selectedPersonnelIds.length === 0}
          startIcon={saving ? <CircularProgress size={20} /> : <CheckCircleIcon />}
        >
          {saving ? t('courierAddModal.actions.saving') : t('courierAddModal.actions.saveCouriers', { count: selectedPersonnelIds.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCourierModal;
